import { html, nothing } from 'lit';
import { dateOnly, number } from '../../shared/ui/format.js';
import { checkout, startCheckout, billingAction, billingRead, accountOverview } from '../api/developer.js';
import { accountPath, stateVariant, word } from '../accounts.js';
import { openPaddle } from '../paddle.js';
import { AccountView } from './account-view.js';

/** Inline payment, with seat access read back from the server after verification by Paddle. */
export class UserAccountBilling extends AccountView
{
   static section = 'billing';
   static detailedHistory = false;
   static properties = {
      offer: { state: true }, seats: { state: true }, opening: { state: true },
      billing: { state: true }, preview: { state: true }, checkoutId: { state: true },
      checkoutStatus: { state: true }, checkoutMessage: { state: true }, checkoutMode: { state: true },
      checkoutData: { state: true }, confirmingCancel: { state: true }, editingSeats: { state: true },
      transactions: { state: true }, activity: { state: true }, nextCursor: { state: true },
      historyLoading: { state: true }, historyError: { state: true }, activityError: { state: true },
      activityCursor: { state: true }, activityLoading: { state: true }, activitySubscription: { state: true },
      refreshing: { state: true }, downloading: { state: true }, confirmingKeep: { state: true }, expandedInvoices: { state: true }
   };

   constructor()
   {
      super();
      this.offer = null;
      this.seats = 1;
      this.opening = false;
      this.billing = null;
      this.preview = null;
      this.checkoutId = null;
      this.checkoutData = null;
      this.checkoutStatus = null;
      this.checkoutMessage = null;
      this.checkoutMode = 'purchase';
      this._checkoutAttempt = 0;
      this.confirmingCancel = false;
      this.transactions = [];
      this.activity = [];
      this.activityCursor = null;
      this.activityLoading = false;
      this.activitySubscription = null;
      this.confirmingKeep = false;
      this.expandedInvoices = [];
      this.editingSeats = false;
      this.refreshing = false;
      this.historyLoading = false;
      this.frameTarget = 'paddle-' + crypto.randomUUID();
   }

   async load(signal)
   {
      this.stopCheckout();
      this.offer = null;
      this.transactions = [];
      this.activity = [];
      this.activityCursor = null;
      this.activityLoading = false;
      this.activitySubscription = null;
      this.historyError = null;
      this.activityError = null;
      this.nextCursor = null;
      this.expandedInvoices = [];
      this.confirmingKeep = false;
      this.editingSeats = false;
      this.billing = null;
      this.preview = null;
      this.confirmingCancel = false;
      const account = this.account;
      await super.load(signal);
      if (signal.aborted || account !== this.account) return;
      if (this.team)
      {
         const offer = await checkout(account, signal);
         if (signal.aborted || account !== this.account) return;
         this.offer = offer;
         if (this.offer.configured)
         {
            try
            {
               const billing = await billingAction(account, 'refresh', undefined, signal);
               if (signal.aborted || account !== this.account) return;
               this.billing = billing;
               const team = await accountOverview(account, signal);
               if (signal.aborted || account !== this.account) return;
               this.team = team;
            }
            catch (error)
            {
               if (!signal.aborted) this.notice = { variant: 'warning', message: billingError(error) };
            }
         }
         if (signal.aborted || account !== this.account) return;
         this.seats = Math.max(1, this.team.usage?.seatQuota || this.team.usage?.billable || 1);
         if (this.offer.configured) await this.loadHistory(false, signal);
      }
   }

   disconnectedCallback()
   {
      this.stopCheckout();
      super.disconnectedCallback();
   }

   stopCheckout()
   {
      this._checkoutAttempt++;
      this._checkoutShown?.();
      this._checkoutShown = null;
      clearTimeout(this._poll);
      this.closePaymentForm();
      if (this.checkoutStatus !== null) this.opening = false;
      this.checkoutStatus = null;
      this.checkoutMessage = null;
      this.checkoutData = null;
   }

   closePaymentForm()
   {
      clearTimeout(this._frameLoad);
      this._closeCheckout?.();
      this._closeCheckout = null;
      this.checkoutId = null;
   }

   get managed()
   {
      return !!this.billing?.subscriptionId && this.billing.status !== 'canceled';
   }


   get description()
   {
      return this.team ? `Plan, seats and invoices for ${this.team.name}.` : '';
   }

   get cancelScheduled()
   {
      return this.billing?.scheduledChange?.action === 'cancel';
   }


   get pageActions()
   {
      return [{ id: 'refresh-billing', title: 'Refresh billing', label: this.refreshing ? 'Refreshing…' : 'Refresh billing',
         icon: 'fa-solid fa-rotate', variant: 'outline-secondary', disabled: this.refreshing || this.opening }];
   }

   onPageAction(id)
   {
      if (id === 'refresh-billing') void this.refresh();
   }

   get body()
   {
      return html`
         ${this.offer?.environment === 'sandbox' ? html`<div class="d-flex align-items-center gap-2">
            <lia-badge text="Sandbox" variant="secondary" subtle></lia-badge>
            <span class="small text-body-secondary">Test payments only</span>
         </div>` : nothing}
         ${this.team.usage?.overQuota ? this.overQuota : nothing}
         <div class="row g-3">
            <div class="col-xl-7">${this.plan}</div>
            <div class="col-xl-5">${this.capacity}</div>
         </div>
         ${this.checkoutModal}
         <div class="row g-3 align-items-start">
            <div class="col-xl-8">${this.invoices}</div>
            <div class="col-xl-4">${this.activityPanel}</div>
         </div>
         <p class="small text-body-secondary mb-0">Payments are processed securely by Paddle.
            <a href="https://covdbg.com/company/contact/">Get billing help</a>
            <span aria-hidden="true"> · </span>
            <a href="https://covdbg.com/imprint/terms/" target="_blank" rel="noopener">Terms & refund policy</a>
         </p>`;
   }


   get overQuota()
   {
      const usage = this.team.usage ?? {};
      return html`<lia-alert variant="warning" icon="fa-solid fa-triangle-exclamation"
         heading="Your team needs more seats"
         message=${`${number(usage.billable)} seats need a paid license; your plan covers ${number(usage.seatQuota)}. Add seats or remove members to stay within your allowance. Analysis keeps running during the grace period.`}>
      </lia-alert>`;
   }


   get plan()
   {
      const totals = this.billing?.recurringTotals;
      const upcoming = this.billing?.nextTransaction?.details?.totals;
      const currency = this.billing?.currencyCode || 'USD';
      const paid = this.team.billed || this.managed;
      const scheduled = this.cancelScheduled;
      const status = this.billing?.status;
      const ended = status === 'canceled';
      const paused = status === 'paused';
      return html`
         <lia-card .title=${'Current plan'} icon="fa-solid fa-layer-group" .cardClass=${'h-100 mb-0'}
            .content=${html`
               <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                  <h2 class="h4 mb-0">${word(this.team.plan || 'free')}</h2>
                  <lia-badge .text=${scheduled ? 'Ending soon' : status === 'past_due' ? 'Payment overdue' : word(status || this.team.planState)}
                     variant=${scheduled ? 'warning' : stateVariant(this.team.planState)} subtle></lia-badge>
                  ${this.team.ossGrant ? html`<lia-badge text="OSS grant" variant="info" subtle></lia-badge>` : nothing}
               </div>
               <p class="small text-body-secondary mb-3">${paid ? 'Private repositories and team CI, covered by developer seats.' : 'Public repositories are free. Add seats for private team projects.'}</p>
               <div class="d-flex flex-wrap align-items-baseline gap-2">
                  <strong class="fs-2 fw-semibold">${ended || paused ? '—' : totals ? money(totals.grand_total ?? totals.total, currency, true) : paid ? 'Unavailable' : '$12'}</strong>
                  <span class="text-body-secondary">${ended ? 'Subscription ended' : paused ? 'Billing paused' : paid ? `${currency} / month` : 'USD / seat / month'}</span>
               </div>
               <p class="small text-body-secondary mt-1 mb-3">${ended ? 'Start a new subscription below. Your previous invoices stay in Billing history.' : paused ? 'Paid seats are unavailable while this subscription is paused.' : totals
                  ? `${number(this.team.usage?.seatQuota ?? 0)} paid seats · ${money(totals.subtotal, currency, true)} subtotal${Number(totals.discount) ? ` − ${money(totals.discount, currency, true)} discount` : ''} + ${money(totals.tax, currency, true)} tax`
                  : paid ? 'Refresh billing to check your current payment amount.' : 'Billed monthly. Tax is shown at checkout.'}</p>
               ${scheduled ? html`<div class="row g-3">
                  <div class="col-sm-6"><lia-key-value bare label="Renewal" value="Canceled. No further renewal payment."></lia-key-value></div>
                  <div class="col-sm-6"><lia-key-value bare label="Seats available until"
                     value=${billingDate(this.billing.scheduledChange.effective_at)}></lia-key-value></div>
               </div>`
               : html`<lia-key-value bare icon="fa-regular fa-calendar"
                  label=${ended || paused ? 'No renewal scheduled' : this.billing?.nextBilledAt ? `Next payment · ${billingDate(this.billing.nextBilledAt)}`
                     : this.team.currentPeriodEnd ? `Current period ends ${dateOnly(this.team.currentPeriodEnd)}` : 'Monthly billing, flexible seat count'}
                  value=${ended ? 'Your previous invoices remain available below.' : upcoming
                     ? `${money(upcoming.grand_total ?? upcoming.total, currency, true)} including tax. Seat changes may change this amount.`
                     : 'Review the amount before you confirm a purchase or seat change.'}></lia-key-value>`}
               ${this.buy}
               ${this.managed && this.billing.collectionMode === 'automatic' && ['active', 'past_due'].includes(status) ? html`
                  <button class="btn btn-outline-secondary mt-3" ?disabled=${this.opening || this.refreshing}
                     @click=${() => this.openCheckout('payment')}>Update payment method</button>` : nothing}
            `}></lia-card>`;
   }



   get capacity()
   {
      const usage = this.team.usage ?? {};
      const paid = usage.seatQuota ?? 0;
      const free = usage.includedFreeSeats ?? 0;
      const used = usage.countingSeats ?? 0;
      const allowed = paid + free;
      const available = Math.max(0, allowed - used);
      const percent = allowed > 0 ? Math.min(100, Math.round(used / allowed * 100)) : used ? 100 : 0;
      return html`
         <lia-card .title=${'Seat usage'} icon="fa-solid fa-users" .cardClass=${'h-100 mb-0'}
            .headerActions=${[{ id: 'members', label: 'Manage members', href: accountPath('members', this.account), variant: 'outline-secondary', size: 'sm' }]}
            .content=${html`
               <div class="d-flex align-items-baseline gap-2 mb-1"><strong class="fs-2 fw-semibold">${number(used)}</strong>
                  <span class="fs-5 text-body-secondary">/ ${number(allowed)}</span><span class="small text-body-secondary">seats occupied</span></div>
               <lia-progress thin .percent=${percent} .valueNow=${used} .valueMax=${Math.max(1, allowed)}
                  variant=${usage.overQuota ? 'warning' : 'primary'} bar-label="Seat usage"
                  text=${usage.overQuota ? `${number(used - allowed)} more seats needed` : `${number(available)} seats available`}></lia-progress>
               <div class="row g-3 mt-1">
                  <div class="col-6"><lia-key-value bare label="Active members" value=${number(usage.activeSeats ?? 0)}></lia-key-value></div>
                  <div class="col-6"><lia-key-value bare label="Pending seats" value=${number(usage.pendingSeats ?? 0)}></lia-key-value></div>
                  <div class="col-6"><lia-key-value bare label="Purchased seats" value=${number(paid)}></lia-key-value></div>
                  ${free ? html`<div class="col-6"><lia-key-value bare label="Complimentary seats" value=${number(free)}></lia-key-value></div>` : nothing}
               </div>
            `}
            .footer=${html`<span class="small text-body-secondary">You pay for purchased seats, including unassigned seats. Removing a member does not reduce your bill.</span>`}>
         </lia-card>`;
   }


   get buy()
   {
      if (!this.offer?.configured) return html`<p class="small mt-4 mb-0">Seat purchases are not available here yet. <a href="https://covdbg.com/company/contact/">Contact support</a>.</p>`;
      if (!this.billing) return html`<p class="small mt-3 mb-0">Billing could not be loaded. Use Refresh billing to try again.</p>`;
      if (this.managed && this.billing.status !== 'active') return html`<p class="small mt-3 mb-0">${this.billing.status === 'past_due'
         ? 'A payment is overdue. Update your payment method to pay the balance shown in checkout. Your seats remain available while payment is being resolved.'
         : 'Contact billing support to manage this subscription.'}</p>`;
      if (this.cancelScheduled) return html`
         <div class="mt-3">
            ${this.confirmingKeep ? html`
               <p class="small">Keep ${number(this.team.usage?.seatQuota ?? 0)} seats and continue monthly billing?
                  Your next renewal is ${billingDate(this.billing.scheduledChange.effective_at)}.
                  The current monthly total is ${money(this.billing.recurringTotals?.grand_total ?? this.billing.recurringTotals?.total, this.billing.currencyCode, true)}, including tax.
                  No extra charge is made now.</p>
               <div class="d-flex flex-wrap gap-2">
                  <button class="btn btn-primary btn-sm" ?disabled=${this.opening} @click=${() => this.run(() => billingAction(this.account, 'keep'), 'Cancellation removed. Your subscription will renew monthly.')}>Confirm renewal</button>
                  <button class="btn btn-outline-secondary btn-sm" ?disabled=${this.opening} @click=${() => { this.confirmingKeep = false; }}>Leave cancellation scheduled</button>
               </div>` : html`
               <button class="btn btn-outline-primary btn-sm" ?disabled=${this.opening || this.refreshing} @click=${() => { this.confirmingKeep = true; }}>Keep subscription</button>
               <p class="small text-body-secondary mt-2 mb-0">Keep your subscription to resume monthly renewals.</p>`}
         </div>`;
      return html`
         <div class="mt-3">
            ${this.managed && !this.editingSeats ? html`
               <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
                  <button class="btn btn-primary" ?disabled=${this.opening || this.refreshing}
                     @click=${() => { this.editingSeats = true; this.seats = this.team.usage?.seatQuota || 1; }}>Change seats</button>
                  <button class="btn btn-link btn-sm text-body-secondary" ?disabled=${this.opening || this.refreshing}
                     @click=${() => { this.confirmingCancel = !this.confirmingCancel; }}>Cancel subscription</button>
               </div>` : html`
               <form @submit=${event => this.open(event)}>
                  <label class="form-label fw-semibold" for="seat-count">${this.managed ? 'New seat count' : 'How many seats do you need?'}</label>
                  <div class="d-flex flex-wrap align-items-center gap-2">
                     <input id="seat-count" class="form-control covdbg-count" type="number" min="1" max="1000" step="1" required
                        aria-describedby="seat-price-note" .value=${String(this.seats)} ?disabled=${this.opening || !!this.checkoutId || this.refreshing}
                        @input=${event => { this.seats = Number(event.target.value); this.preview = null; }}>
                     <button class="btn btn-primary" type="submit" ?disabled=${this.opening || !this.billing || !!this.checkoutId || this.refreshing}>
                        ${this.opening ? 'Please wait…' : this.managed ? 'Preview seat change' : 'Continue to checkout'}
                     </button>
                     ${this.managed ? html`<button class="btn btn-link" type="button" ?disabled=${this.opening}
                        @click=${() => { this.editingSeats = false; this.preview = null; }}>Close</button>` : nothing}
                  </div>
                  <p class="small text-body-secondary mt-2 mb-0" id="seat-price-note">$12 USD per seat per month, plus tax.</p>
                  ${this.managed ? html`<p class="small text-body-secondary mt-2 mb-0">Added seats are charged for the rest of your billing period. Removed seats create credit for future invoices.
                     <a href="https://covdbg.com/docs/reference/licensing-faq/#how-are-seat-changes-billed" target="_blank" rel="noopener">How seat billing works</a></p>` : nothing}
               </form>
               ${this.preview ? this.changePreview : nothing}
            `}
            ${this.confirmingCancel ? html`
               <div class="border rounded p-3 mt-3" role="region" aria-label="Confirm cancellation">
                  <h3 class="h6">End your subscription?</h3>
                  <p class="small">Your team keeps its seats until ${billingDate(this.team.currentPeriodEnd)}. No further renewal will be charged.</p>
                  <div class="d-flex flex-wrap gap-2">
                     <button class="btn btn-danger btn-sm" ?disabled=${this.opening}
                        @click=${() => this.run(() => billingAction(this.account, 'cancel'), 'Cancellation scheduled.')}>Confirm cancellation</button>
                     <button class="btn btn-outline-secondary btn-sm" ?disabled=${this.opening}
                        @click=${() => { this.confirmingCancel = false; }}>Keep subscription</button>
                  </div>
               </div>` : nothing}
         </div>`;
   }

   get changePreview()
   {
      const preview = this.preview;
      const immediate = preview.immediateTransaction?.details?.totals;
      const next = preview.nextTransaction?.details?.totals;
      const recurring = preview.recurringTransactionDetails?.totals;
      const result = preview.updateSummary?.result;
      const credit = result?.action === 'credit' ? Math.abs(Number(result.amount)) : Number(immediate?.credit_to_balance || 0);
      const charge = immediate?.balance ?? immediate?.grand_total ?? (result?.action === 'charge' ? result.amount : 0);
      const renewal = preview.nextTransaction?.billing_period?.starts_at || this.billing.nextBilledAt;
      return html`
         <div class="border rounded p-3 mt-3" aria-live="polite">
            <h3 class="h6">Review your change · ${number(this.team.usage?.seatQuota)} → ${number(preview.quantity)} seats</h3>
            <div class="row g-3 mb-3">
               <div class="col-sm-6"><lia-key-value bare label="Payment due now" value=${money(charge, preview.currencyCode, true)}></lia-key-value></div>
               ${credit > 0 ? html`<div class="col-sm-6"><lia-key-value bare label="Credit for future invoices" value=${money(credit, preview.currencyCode, true)}></lia-key-value></div>` : nothing}
               <div class="col-sm-6"><lia-key-value bare label="New monthly total" value=${money(recurring?.grand_total ?? recurring?.total, preview.currencyCode, true)}></lia-key-value></div>
               ${renewal ? html`<div class="col-sm-6"><lia-key-value bare label="Next renewal" value=${billingDate(renewal)}></lia-key-value></div>` : nothing}
               ${next ? html`<div class="col-sm-6"><lia-key-value bare label="Next payment" value=${money(next.balance ?? next.grand_total ?? next.total, preview.currencyCode, true)}></lia-key-value></div>` : nothing}
            </div>
            <p class="small text-body-secondary">${recurring ? `${money(recurring.subtotal, preview.currencyCode, true)} monthly subtotal + ${money(recurring.tax, preview.currencyCode, true)} tax.` : ''}
               Changes take effect immediately. Your renewal date stays the same.
               ${credit > 0 ? 'Credit is applied to future invoices, not refunded to your card.' : 'If payment fails, your existing seat count stays unchanged.'}</p>
            ${preview.quantity < (this.team.usage?.billable ?? 0) ? html`<lia-alert variant="warning" heading="Your team will have more members than paid seats"
               message="Remove members or choose enough seats to cover them. A reduction starts the allowance grace period."></lia-alert>` : nothing}
            <button class="btn btn-primary btn-sm" ?disabled=${this.opening}
               @click=${() => this.run(() => billingAction(this.account, 'seats', {
                  quantity: preview.quantity, updatedAt: preview.updatedAt
               }), 'Seat count updated.')}>Confirm seat change</button>
         </div>`;
   }


   get invoices()
   {
      return html`
         <lia-card .title=${'Billing history'} subtitle="Invoices and payments for this team, including previous subscriptions." icon="fa-solid fa-file-invoice"
            .headerActions=${this.constructor.detailedHistory ? [] : [{ id: 'history', label: 'View full history', href: accountPath('billing/history', this.account), variant: 'outline-secondary', size: 'sm' }]}
            .content=${html`
               ${this.historyError ? html`<lia-alert variant="warning" message=${this.historyError}
                  .actions=${[{ id: 'retry', label: 'Retry history', variant: 'outline-secondary', size: 'sm' }]}
                  @lia-action=${() => this.loadHistory()}></lia-alert>` : nothing}
               <lia-table attached caption="Team invoices, newest first. Amounts include tax." caption-hidden
                  ?loading=${this.historyLoading}
                  .expandedKeys=${this.expandedInvoices}
                  .listing=${{
                     id: 'billing-invoices', noSearch: true, noColumnManager: true,
                     rows: this.constructor.detailedHistory ? this.transactions : this.transactions.slice(0, 3), rowKey: row => row.id,
                     rowDetail: this.constructor.detailedHistory ? row => this.invoiceDetails(row) : undefined,
                     emptyTitle: this.historyError ? 'History unavailable' : 'No invoices yet',
                     emptyMessage: this.historyError ? 'Try again when the connection is restored.' : 'Your subscription payments and seat adjustments will appear here.',
                     columns: [
                        { key: 'invoiceNumber', label: 'Invoice', render: row => html`<span>${invoiceLabel(row.origin)}</span>
                           <div class="small text-body-secondary">${dateOnly(row.billedAt || row.createdAt)}${row.invoiceNumber ? ` · ${row.invoiceNumber}` : ''}</div>
                           ${this.constructor.detailedHistory ? html`<button class="btn btn-link btn-sm p-0" aria-expanded=${this.expandedInvoices.includes(row.id)}
                              aria-label=${`${this.expandedInvoices.includes(row.id) ? 'Hide' : 'View'} details for ${row.invoiceNumber || invoiceLabel(row.origin)}`} aria-controls=${`billing-invoices-detail-${row.id}`} @click=${() => {
                                 this.expandedInvoices = this.expandedInvoices.includes(row.id)
                                    ? this.expandedInvoices.filter(id => id !== row.id) : [...this.expandedInvoices, row.id];
                              }}>${this.expandedInvoices.includes(row.id) ? 'Hide details' : 'View details'}</button>` : nothing}` },
                        { key: 'status', label: 'Status', render: row => html`<lia-badge .text=${paymentLabel(row.status, row.origin)} variant=${paymentVariant(row.status)} subtle></lia-badge>
                           ${row.adjustments?.refund && Number(row.adjustments.refund.total) > 0 ? html`<div class="small text-body-secondary">Refund: ${money(row.adjustments.refund.total, row.currencyCode, true)}</div>` : nothing}
                           ${row.adjustments?.credit && Number(row.adjustments.credit.total) > 0 ? html`<div class="small text-body-secondary">Credit: ${money(row.adjustments.credit.total, row.currencyCode, true)}</div>` : nothing}` },
                        { key: 'total', label: 'Amount', class: 'text-end', render: row => html`<span class="fw-semibold">${money(row.total, row.currencyCode, true)}</span>
                           <div class="small text-body-secondary">${row.currencyCode} · tax ${money(row.tax, row.currencyCode, true)}</div>` }
                     ],
                     rowActions: row => row.hasInvoice ? [{ id: 'invoice', label: 'PDF', icon: 'fa-solid fa-arrow-down',
                        title: `Download invoice ${row.invoiceNumber}`, variant: 'outline-secondary', size: 'sm', disabled: !!this.downloading }] : []
                  }}
                  @lia-action=${event => { if (event.detail?.row) void this.downloadInvoice(event.detail.row); }}>
               </lia-table>
            `}
            .footer=${this.constructor.detailedHistory && this.nextCursor ? html`<div class="text-center"><button class="btn btn-outline-secondary btn-sm"
               ?disabled=${this.historyLoading} @click=${() => this.loadHistory(true)}>${this.historyLoading ? 'Loading…' : 'Load older invoices'}</button></div>` : nothing}>
         </lia-card>`;
   }

   invoiceDetails(row)
   {
      return html`<div class="text-wrap">
         <div class="row g-3 mb-3">
            <div class="col-sm-6"><lia-key-value bare label="Issued" value=${billingDate(row.billedAt || row.createdAt)}></lia-key-value></div>
            ${row.period ? html`<div class="col-sm-6"><lia-key-value bare label="Billing period"
               value=${`${billingDate(row.period.starts_at)} to ${billingDate(row.period.ends_at)}`}></lia-key-value></div>` : nothing}
            ${[['Subtotal', row.subtotal], ['Tax', row.tax], ['Discount', row.discount], ['Credit applied', row.credit], ['Outstanding', row.balance]]
               .filter(([, value]) => value != null).map(([label, value]) => html`<div class="col-sm-4 col-6">
                  <lia-key-value bare label=${label} value=${money(value, row.currencyCode, true)}></lia-key-value></div>`)}
         </div>
         ${row.items?.length ? html`<div class="list-group list-group-flush">
            ${row.items.map(item => html`<div class="list-group-item px-0">
               <div class="fw-semibold">${item.name} · ${number(item.quantity)} ${item.quantity === 1 ? 'seat' : 'seats'}</div>
               ${item.proration?.billing_period ? html`<p class="small text-body-secondary mb-2">
                  Prorated: ${billingDate(item.proration.billing_period.starts_at)} to ${billingDate(item.proration.billing_period.ends_at)}
               </p>` : nothing}
               <div class="row g-2 mt-1">
                  ${[['Subtotal', item.subtotal], ['Tax', item.tax], ['Total', item.total]].map(([label, value]) => html`
                     <div class="col-4"><lia-key-value bare label=${label} value=${money(value, row.currencyCode, true)}></lia-key-value></div>`)}
               </div>
            </div>`)}
         </div>` : nothing}</div>`;
   }


   get activityPanel()
   {
      return html`
         <lia-card .title=${this.constructor.detailedHistory ? 'Subscription activity' : 'Recent activity'} subtitle="Changes to your subscription." icon="fa-solid fa-clock-rotate-left" no-body
            .content=${html`
               ${this.constructor.detailedHistory && this.billing?.previousPaddleSubscriptionIds?.length ? html`<div class="p-3">
                  <label class="form-label" for="activity-subscription">Subscription</label>
                  <select id="activity-subscription" class="form-select" .value=${this.activitySubscription || ''}
                     @change=${event => { this.activitySubscription = event.target.value || null; this.activity = []; this.activityCursor = null; void this.loadActivity(); }}>
                     <option value="">Current subscription</option>
                     ${[...this.billing.previousPaddleSubscriptionIds].reverse().map((id, i) => html`<option value=${id}>Previous subscription ${i + 1}</option>`)}
                  </select>
               </div>` : nothing}
               ${this.activityError ? html`<div class="p-3"><lia-alert variant="warning" message=${this.activityError}></lia-alert></div>` : nothing}
               <div class="list-group list-group-flush">
                  ${(this.constructor.detailedHistory ? this.activity : this.activity.slice(0, 3)).map(entry => html`<lia-key-value
                     label=${activityLabel(entry)} value=${activityDescription(entry)}></lia-key-value>`)}
               </div>
               ${!this.activity.length && !this.activityError ? html`<div class="p-3">
                  <lia-empty-state icon="fa-solid fa-clock-rotate-left" title=${this.historyLoading ? 'Loading activity…' : 'No changes yet'}
                     message="Changes to your plan and seats will appear here."></lia-empty-state>
               </div>` : nothing}
            `}
            .footer=${this.constructor.detailedHistory && this.activityCursor ? html`<div class="text-center">
               <button class="btn btn-outline-secondary btn-sm" ?disabled=${this.activityLoading} @click=${() => this.loadActivity(true)}>
                  ${this.activityLoading ? 'Loading…' : 'Load older activity'}</button></div>` : nothing}></lia-card>`;
   }


   get checkoutModal()
   {
      const status = this.checkoutStatus;
      const confirming = status === 'confirming' || status === 'pending';
      const complete = status === 'confirmed';
      const payment = this.checkoutMode === 'payment';
      return html`
         <lia-modal modal-id="seat-checkout" .open=${status !== null}
            heading=${complete ? payment ? 'Payment details updated' : 'Your seats are ready' : confirming ? 'Confirming with Paddle' : payment ? 'Update payment method' : 'Complete your purchase'}
            size="lg" dialog-class="modal-fullscreen-sm-down" body-class="p-0" centered scrollable
            .closeLabel=${'Close checkout'}
            .footerActions=${[{ id: 'close-checkout', label: complete ? 'Back to billing' : 'Close checkout', variant: complete ? 'primary' : 'outline-secondary' }]}
            @lia-action=${event => { if (event.detail.id === 'close-checkout') event.currentTarget.hide(); }}
            @lia-modal-show=${event => {
               this._checkoutShown?.();
               this._checkoutShown = null;
               if (this.checkoutStatus === null) event.currentTarget.hide();
            }}
            @lia-modal-hide=${this.onCheckoutHidden}
            .content=${html`
               <div class="p-3 p-sm-4 pb-0" aria-live="polite">
                  <p class="small text-body-secondary">${this.team.name} · Secure payment by Paddle
                     ${this.offer?.environment === 'sandbox' ? html`<lia-badge text="Sandbox" variant="secondary" subtle></lia-badge>` : nothing}</p>
                  ${complete ? html`<lia-alert variant="success"
                     message=${payment ? 'Paddle confirmed your updated payment details.' : `${number(this.billing?.quantity)} paid seats are ready. Invite your team from Manage members.`}></lia-alert>`
                     : confirming ? html`<lia-alert variant="info"
                        heading=${status === 'pending' ? 'Confirmation is taking longer than usual' : 'Submitted to Paddle'}
                        message="You can close this window safely. Use Refresh billing to check for confirmation."></lia-alert>`
                     : status === 'error' ? nothing : html`
                        ${this.summary}
                        ${status === 'loading' ? html`<p class="small text-body-secondary" role="status">
                           <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>Loading secure payment form…</p>` : nothing}
                     `}
                  ${this.checkoutMessage ? html`<lia-alert variant="warning" message=${this.checkoutMessage}></lia-alert>` : nothing}
               </div>
               <div class=${this.frameTarget} ?hidden=${!this.checkoutId || confirming || complete || status === 'error'}></div>
               <p class="small text-body-secondary px-3 px-sm-4 pb-2 mb-0">${complete
                  ? 'Your invoices and subscription changes are available in Billing history.'
                  : confirming ? 'Please wait for confirmation before trying another payment.'
                  : 'Closing this window does not cancel a payment that has already been submitted.'}</p>
            `}></lia-modal>`;
   }

   async onCheckoutHidden()
   {
      const trigger = this._checkoutTrigger;
      this._checkoutTrigger = null;
      this.stopCheckout();
      const attempt = this._checkoutAttempt;
      await this.updateComplete;
      if (attempt === this._checkoutAttempt) (trigger?.isConnected ? trigger : this.querySelector('button.btn-primary'))?.focus();
   }

   get summary()
   {
      const data = this.checkoutData;
      return html`
         <div class="border rounded p-3 mb-3" aria-live="polite">
            <p class="mb-1">${this.checkoutMode === 'payment'
               ? this.billing?.status === 'past_due' ? 'Pay the overdue balance and update your payment method.' : 'Update the payment method used for future renewals. No payment is due now.'
               : `covdbg Team · ${data?.items?.[0]?.quantity ?? this.seats} developer seats · billed monthly`}</p>
            ${data?.totals ? html`
               ${this.checkoutMode !== 'payment' || Number(data.totals.total) > 0 ? html`<p class="small mb-1">Subtotal: ${money(data.totals.subtotal, data.currency_code)} · Tax: ${money(data.totals.tax, data.currency_code)}</p>` : nothing}
               <p class="fw-semibold mb-1">Total today: ${money(data.totals.total, data.currency_code)}</p>
               ${this.checkoutMode !== 'payment' && data.recurring_totals ? html`<p class="small mb-1">Monthly renewal: ${money(data.recurring_totals.total, data.currency_code)}</p>` : nothing}
            ` : html`<p class="small mb-1">Loading your payment total…</p>`}
            ${this.checkoutMode === 'payment' && this.cancelScheduled ? html`<p class="small mb-0">Your scheduled cancellation stays in place.</p>` : nothing}
         </div>`;
   }

   async loadHistory(older = false, signal)
   {
      const account = this.account;
      const request = this._historyRequest = (this._historyRequest || 0) + 1;
      this.historyLoading = true;
      this.historyError = null;
      const activity = older ? Promise.resolve() : this.loadActivity(false, signal);
      try
      {
         const result = await billingRead(account, 'history' + (older && this.nextCursor ? `?after=${encodeURIComponent(this.nextCursor)}` : ''), signal);
         if (signal?.aborted || account !== this.account || !this.isConnected || request !== this._historyRequest) return;
         this.transactions = older ? [...this.transactions, ...result.transactions] : result.transactions;
         this.nextCursor = result.nextCursor;
      }
      catch
      {
         if (!signal?.aborted && account === this.account && request === this._historyRequest)
            this.historyError = 'Invoices could not be loaded. Try again; your seats are unaffected.';
      }
      finally
      {
         if (request === this._historyRequest) this.historyLoading = false;
         await activity;
      }
   }

   async loadActivity(older = false, signal)
   {
      const account = this.account;
      const request = this._activityRequest = (this._activityRequest || 0) + 1;
      this.activityLoading = true;
      this.activityError = null;
      const query = new URLSearchParams();
      if (this.activitySubscription) query.set('subscriptionId', this.activitySubscription);
      if (older && this.activityCursor) query.set('after', this.activityCursor);
      try
      {
         const result = await billingRead(account, 'activity' + (query.size ? `?${query}` : ''), signal);
         if (signal?.aborted || account !== this.account || !this.isConnected || request !== this._activityRequest) return;
         this.activity = older ? [...this.activity, ...result.entries] : result.entries;
         this.activityCursor = result.nextCursor;
      }
      catch
      {
         if (!signal?.aborted && account === this.account && request === this._activityRequest)
            this.activityError = 'Subscription activity could not be loaded. Use Refresh billing to try again.';
      }
      finally { if (request === this._activityRequest) this.activityLoading = false; }
   }

   async downloadInvoice(row)
   {
      this.downloading = row.id;
      const account = this.account;
      const popup = window.open('about:blank', '_blank');
      if (popup) popup.opener = null;
      try
      {
         const result = await billingRead(account, `invoices/${encodeURIComponent(row.id)}`);
         if (account !== this.account) { popup?.close(); return; }
         if (popup) popup.location.href = result.url;
         else window.location.assign(result.url);
      }
      catch
      {
         popup?.close();
         if (account === this.account) this.notice = { variant: 'warning', message: 'The invoice could not be downloaded. Please try again.' };
      }
      finally { this.downloading = null; }
   }


   async open(event)
   {
      event.preventDefault();
      if (!this.offer?.configured || this.opening || !this.billing) return;
      if (!Number.isInteger(this.seats) || this.seats < 1 || this.seats > 1000)
      {
         this.notice = { variant: 'warning', message: 'Choose between 1 and 1,000 seats.' };
         return;
      }
      if (!this.managed) return this.openCheckout('purchase');
      if (this.seats === this.billing.quantity)
      {
         this.notice = { variant: 'info', message: 'Choose a different seat count to preview a change.' };
         return;
      }
      const account = this.account;
      this.opening = true;
      this.notice = null;
      try
      {
         const preview = await billingAction(account, 'preview', { quantity: this.seats });
         if (this.isConnected && account === this.account) this.preview = preview;
      }
      catch (error)
      {
         if (account === this.account) this.notice = { variant: 'warning', message: billingError(error) };
      }
      finally { if (account === this.account) this.opening = false; }
   }

   async openCheckout(mode)
   {
      if (this.opening || this.checkoutStatus !== null) return;
      const account = this.account;
      const attempt = ++this._checkoutAttempt;
      this.opening = true;
      this.notice = null;
      this.checkoutMode = mode;
      this._checkoutTrigger = document.activeElement;
      const shown = new Promise(resolve => { this._checkoutShown = resolve; });
      this.checkoutMessage = null;
      this.checkoutStatus = 'loading';
      try
      {
         const transaction = mode === 'payment'
            ? await billingAction(account, 'payment-method')
            : await startCheckout(account, this.seats);
         await shown;
         if (!this.isConnected || account !== this.account || attempt !== this._checkoutAttempt) return;
         this.checkoutId = transaction.transactionId;
         this.seats = transaction.quantity;
         await this.updateComplete;
         const close = await openPaddle(this.offer, {
            transactionId: this.checkoutId,
            customer: mode === 'purchase' && this.offer.email ? { email: this.offer.email } : undefined,
            settings: {
               displayMode: 'inline', variant: 'one-page', frameTarget: this.frameTarget,
               frameInitialHeight: '450', frameStyle: 'width:100%; min-width:312px; background-color:transparent; border:none;',
               theme: document.documentElement.dataset.bsTheme === 'dark' ? 'dark' : 'light'
            }
         }, event => this.onCheckoutEvent(event, account), () => this.isConnected && account === this.account && attempt === this._checkoutAttempt && this.checkoutId === transaction.transactionId);
         if (!this.isConnected || account !== this.account || attempt !== this._checkoutAttempt) close();
         else
         {
            this._closeCheckout = close;
            if (!this.checkoutData)
            {
               this._frameLoad = setTimeout(() =>
               {
                  this.closePaymentForm();
                  this.checkoutStatus = 'error';
                  this.checkoutMessage = 'The payment form did not load. Check your connection or content blocker, then close checkout and try again.';
               }, 20000);
            }
         }
      }
      catch (error)
      {
         if (account === this.account && attempt === this._checkoutAttempt)
         {
            this.closePaymentForm();
            this.checkoutStatus = 'error';
            this.checkoutMessage = billingError(error);
         }
      }
      finally { if (attempt === this._checkoutAttempt) this.opening = false; }
   }

   onCheckoutEvent(event, account)
   {
      if (account !== this.account || !this.isConnected || !this.checkoutId) return;
      if (event.name === 'checkout.error' && !event.data?.transaction_id)
      {
         this.checkoutMessage = 'The payment form reported an error. Close checkout and refresh billing before retrying.';
         return;
      }
      if (event.data?.transaction_id !== this.checkoutId) return;
      if (event.name === 'checkout.loaded' || event.name === 'checkout.updated')
      {
         clearTimeout(this._frameLoad);
         this.checkoutData = event.data;
         this.querySelector(`.${this.frameTarget} iframe`)?.setAttribute('title', 'Secure payment form by Paddle');
         this.checkoutStatus = 'payment';
         this.checkoutMessage = null;
      }
      if (event.name === 'checkout.completed')
      {
         this.closePaymentForm();
         this.checkoutStatus = 'confirming';
         this.checkoutMessage = null;
         this.notice = { variant: 'info', message: 'Submitted to Paddle. Checking for confirmation… You can leave this page safely.' };
         void this.pollBilling(account, 0);
      }
      if (event.name === 'checkout.error')
      {
         this.checkoutMessage = 'Payment could not be completed. Check the message in the payment form, or close checkout and refresh billing before retrying.';
      }
   }

   async pollBilling(account, attempt, checkoutAttempt = this._checkoutAttempt)
   {
      if (!this.isConnected || account !== this.account || checkoutAttempt !== this._checkoutAttempt) return;
      const refreshed = await this.refresh(false);
      if (!this.isConnected || account !== this.account || checkoutAttempt !== this._checkoutAttempt) return;
      if (refreshed && (this.checkoutMode === 'payment' ? this.billing?.paymentUpdateStatus === 'completed'
         : this.billing?.status === 'active' && this.billing.quantity > 0))
      {
         if (this.checkoutStatus !== null) this.checkoutStatus = 'confirmed';
         this.notice = { variant: 'success', message: this.checkoutMode === 'payment' ? 'Paddle confirmed your updated payment details.' : 'Your subscription is confirmed and your seats are available.' };
      }
      else if (attempt < 20)
      {
         this._poll = setTimeout(() => void this.pollBilling(account, attempt + 1, checkoutAttempt), 3000);
      }
      else
      {
         if (this.checkoutStatus !== null) this.checkoutStatus = 'pending';
         this.notice = { variant: 'info', message: 'Payment is still being confirmed. Billing updates automatically; use Refresh billing to check again.' };
      }
   }

   async refresh(showNotice = true)
   {
      if (this.refreshing) return;
      this.refreshing = true;
      const account = this.account;
      try
      {
         const billing = await billingAction(account, 'refresh');
         const team = await accountOverview(account);
         if (!this.isConnected || account !== this.account) return;
         this.billing = billing;
         this.team = team;
         await this.loadHistory();
         if (showNotice && account === this.account) this.notice = { variant: 'success', message: 'Billing is up to date.' };
         return true;
      }
      catch (error)
      {
         if (account === this.account) this.notice = { variant: 'warning', message: billingError(error) };
         return false;
      }
      finally { this.refreshing = false; }
   }

   async run(action, message)
   {
      if (this.opening) return;
      const account = this.account;
      this.opening = true;
      this.notice = null;
      try
      {
         await action();
         if (account !== this.account) return;
         this.preview = null;
         this.editingSeats = false;
         this.confirmingCancel = false;
         this.confirmingKeep = false;
         const refreshed = await this.refresh(false);
         if (account !== this.account || !this.isConnected) return;
         this.notice = refreshed ? { variant: 'success', message }
            : { variant: 'warning', message: 'Your change was saved, but billing could not be refreshed. Use Refresh billing to check the updated plan.' };
      }
      catch (error)
      {
         if (account === this.account)
         {
            this.preview = null;
            this.notice = { variant: 'warning', message: billingError(error) };
         }
      }
      finally
      {
         this.opening = false;
      }
   }
}

function billingError(error)
{
   return error instanceof TypeError || !error.message
      ? 'The connection was interrupted. Refresh billing before trying again.' : error.message;
}

function money(amount, currency = 'USD', minor = false)
{
   if (amount == null) return '—';
   return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' })
      .format(Number(amount) / (minor ? 100 : 1));
}


function billingDate(value)
{
   if (!value) return 'Not scheduled';
   return new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZoneName: 'short' }).format(new Date(value));
}

function activityLabel(entry)
{
   if (entry.action?.startsWith('subscription_scheduled_change_'))
   {
      const action = entry.scheduledAction === 'cancel' ? 'Cancellation' : 'Subscription change';
      const verb = entry.action.endsWith('_removed') ? 'removed' : entry.action.endsWith('_updated') ? 'rescheduled' : 'scheduled';
      return `${action} ${verb}`;
   }
   if (entry.action === 'subscription_item_quantity_updated')
      return entry.quantityDelta == null ? `Purchased seats · ${entry.quantity}` : `Purchased seats · ${entry.quantity - entry.quantityDelta} → ${entry.quantity}`;
   if (entry.action) return word(entry.action.replace(/^subscription_/, '').replaceAll('_', ' '));
   const labels = { SeatQuota: 'Purchased seats', IncludedFreeSeats: 'Complimentary seats', Plan: 'Plan', State: 'Subscription status' };
   return `${labels[entry.field]} · ${word(entry.oldValue ?? 'None')} → ${word(entry.newValue ?? 'None')}`;
}

function activityDescription(entry)
{
   const occurred = billingDate(entry.occurredAt);
   return entry.effectiveAt ? `Requested ${occurred}. Takes effect ${billingDate(entry.effectiveAt)}.` : occurred;
}

function invoiceLabel(origin)
{
   return ({ subscription_update: 'Seat adjustment', subscription_recurring: 'Monthly renewal',
      subscription_charge: 'Additional charge', subscription_payment_method_change: 'Payment method update' })[origin] || 'Subscription';
}

function paymentLabel(status, origin)
{
   if (origin === 'subscription_payment_method_change')
      return ({ completed: 'Updated', draft: 'Not completed', ready: 'Not completed' })[status] || word(status);
   return ({ completed: 'Paid', paid: 'Processing', past_due: 'Payment overdue', billed: 'Awaiting payment',
      draft: 'Draft', ready: 'Ready', canceled: 'Canceled' })[status] || word(status);
}

function paymentVariant(status)
{
   return ({ completed: 'success', past_due: 'danger', billed: 'warning', paid: 'info' })[status] || 'secondary';
}

customElements.define('user-account-billing', UserAccountBilling);
